/**
 * ThemeProvider — production-grade theme management
 *
 * Architecture decisions:
 * - localStorage read is wrapped in try/catch (SSR / private browsing safety)
 * - System theme changes are observed via MediaQueryList listener
 * - setTheme is synchronous for instant UI response; DB write is fire-and-forget
 * - DB errors are ignored (theme is non-critical)
 * - fetchUserTheme is defined outside useEffect to avoid stale closure issues
 * - onAuthStateChange handles TOKEN_REFRESHED explicitly to avoid spurious refetches
 * - storageKey default is app-specific to avoid collisions
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

type Theme = "dark" | "light" | "system";

interface ThemeProviderProps {
  children:      React.ReactNode;
  defaultTheme?: Theme;
  storageKey?:   string;
}

interface ThemeProviderState {
  theme:       Theme;
  setTheme:    (theme: Theme) => void;
  toggleTheme: () => void;
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

/** Safe localStorage read — fails silently in SSR / private browsing. */
function readStorage(key: string): string | null {
  try { return localStorage.getItem(key); }
  catch { return null; }
}

/** Safe localStorage write — fails silently in SSR / private browsing. */
function writeStorage(key: string, value: string): void {
  try { localStorage.setItem(key, value); }
  catch { /* quota exceeded or SSR — ignore */ }
}

const VALID_THEMES = new Set<string>(["dark", "light", "system"]);

function isValidTheme(value: unknown): value is Theme {
  return typeof value === "string" && VALID_THEMES.has(value);
}

/** Resolves "system" to the actual OS preference. */
function resolveTheme(theme: Theme): "dark" | "light" {
  if (theme !== "system") return theme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Applies the resolved theme class to <html>. */
function applyTheme(theme: Theme): void {
  const root = window.document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolveTheme(theme));
}

// ─────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────

const ThemeContext = createContext<ThemeProviderState>({
  theme:       "system",
  setTheme:    () => undefined,
  toggleTheme: () => undefined,
});

// ─────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey   = "office-rhythm-theme",
}: ThemeProviderProps) {

  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = readStorage(storageKey);
    return isValidTheme(stored) ? stored : defaultTheme;
  });

  // Tracks mounted state to prevent state updates after unmount
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ── Apply theme to <html> whenever it changes ───────────────────────────
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // ── Listen for OS-level theme changes when theme === "system" ───────────
  useEffect(() => {
    if (theme !== "system") return;

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  // ── Fetch persisted theme from DB ───────────────────────────────────────
  const fetchUserTheme = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase
        .from("user_settings")
        .select("theme")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (error) {
        return;
      }

      if (isValidTheme(data?.theme) && isMounted.current) {
        setThemeState(data.theme);
        writeStorage(storageKey, data.theme);
      }
    } catch (_err) {
      // Slient fail for non-critical assets
    }
  }, [storageKey]);

  // ── Auth state listener ─────────────────────────────────────────────────
  useEffect(() => {
    // Fetch on mount in case user is already signed in
    fetchUserTheme();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        fetchUserTheme();
      } else if (event === "SIGNED_OUT") {
        if (isMounted.current) {
          setThemeState(defaultTheme);
          writeStorage(storageKey, defaultTheme);
        }
      }
      // TOKEN_REFRESHED — token silently rotated, no theme refetch needed
    });

    return () => subscription.unsubscribe();
  }, [fetchUserTheme, defaultTheme, storageKey]);

  // ── Set theme: instant UI + fire-and-forget DB write ───────────────────
  // Synchronous state update ensures zero-lag UI response.
  // DB persistence is best-effort — a failure here should never crash the app.
  const setTheme = useCallback((newTheme: Theme) => {
    if (!isValidTheme(newTheme)) {
      return;
    }

    setThemeState(newTheme);
    writeStorage(storageKey, newTheme);

    // Upsert to ensure it works even if the row doesn't exist yet
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      supabase
        .from("user_settings")
        .upsert(
          { user_id: session.user.id, theme: newTheme },
          { onConflict: "user_id" }
        )
        .then(({ error: _error }) => {
          // Success/failure is non-blocking
        });
    });
  }, [storageKey]);

  // Cycle through themes: system → dark → light → system
  const toggleTheme = useCallback(() => {
    const next: Theme =
      theme === "system" ? "dark" :
      theme === "dark"   ? "light" :
      "system";
    setTheme(next);
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ─────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────

export function useTheme(): ThemeProviderState {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider.");
  }
  return context;
}
